import { useMutation } from "@tanstack/react-query";
import apiService from "../../api/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { setIsNewUser, setUser } from "./user.reducer";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getProfileCompletionState } from "./profileCompletion";
import {
  extractStudentPreAuthToken,
  extractStudentUserRecord,
  isPendingStudentVerificationResponse,
  studentAuthApi,
} from "./authApi";
import { createStudentVerificationFlow } from "./studentOtpFlow";
import {
  startJourney,
  setDatadogUserContext,
  logLoginSubmitted,
  logLoginApiResult,
  logUserContextSet,
  logPostLoginRoutingDecision,
} from "../../utils/datadogLogger";
import { persistStudentSessionMode } from "../parentFlow/parentFlow";
import { markPendingFirstSignupDemo } from "./firstSignupDemo";
const GENERIC_LOGIN_ERROR = "User Not Found";
const GENERIC_VERIFICATION_SENT_MESSAGE = "Verification code sent to your email.";

const getLoginErrorMessage = (responseData = {}) =>
  String(responseData?.message || "").trim() || GENERIC_LOGIN_ERROR;

export const finalizeStudentLogin = ({
  dispatch,
  navigate,
  profileData,
  shouldShowFirstSignupDemo = false,
}) => {
  persistStudentSessionMode();

  dispatch(setUser({ user: profileData, token: profileData.jwtoken }));

  setDatadogUserContext(profileData);

  logUserContextSet(
    profileData.id || profileData.user_id,
    profileData.email,
    profileData.plan_name,
    profileData.is_paid,
    profileData.year_id
  );

  const { isNameMissing, isDobMissing, isAvatarMissing } =
    getProfileCompletionState(profileData);

  let destination = "/yearsdashboard";
  let reason = "complete_profile";

  if (isNameMissing || isDobMissing || isAvatarMissing) {
    dispatch(setIsNewUser(true));
    destination = "/afterlogin";
    reason = "incomplete_profile";
    navigate("/afterlogin", {
      state: {
        loginData: profileData,
        showNameTab: isNameMissing,
        showDobTab: isDobMissing,
        showAvatarTab: isAvatarMissing,
        shouldShowFirstSignupDemo,
      },
      replace: true,
    });
  } else {
    if (shouldShowFirstSignupDemo) {
      markPendingFirstSignupDemo();
      dispatch(setIsNewUser(true));
    }
    navigate("/yearsdashboard", { replace: true });
  }

  logPostLoginRoutingDecision(
    profileData.id || profileData.user_id,
    profileData.email,
    destination,
    reason
  );
};

const useLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const routeToPendingVerification = (payload, submittedEmail = "") => {
    const responseData = payload?.data || {};
    const verificationData = responseData?.data || payload?.data || {};
    const email = String(verificationData?.email || submittedEmail || "")
      .trim()
      .toLowerCase();

    const nextContext = createStudentVerificationFlow({
      email,
      preAuthToken: extractStudentPreAuthToken(payload),
      verificationType: verificationData?.verification_type || "otp",
      accountType: verificationData?.account_type || "student",
      canResendVerification: verificationData?.can_resend_verification,
      source: "login",
    });

    navigate("/verification", {
      state: {
        email: nextContext.email,
        source: "login",
        successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
      },
      replace: true,
    });
  };

  return useMutation({
    mutationFn: (loginData) => studentAuthApi.login(loginData),
    onSuccess: (data, variables) => {
      console.log("Login successful:", data);
      const responseData = data?.data || {};
      const submittedEmail = String(variables?.email || "").trim().toLowerCase();
      const profileData = extractStudentUserRecord(data, {
        email: submittedEmail,
      });
      const statusCode = String(responseData?.status_code || "").trim();
      const normalizedStatus = String(responseData?.status || "").trim().toLowerCase();

      // Log login API success with response body
      logLoginApiResult(
        profileData?.email || submittedEmail || "unknown@example.com",
        data?.status || 200,
        "login",
        responseData,
        null
      );

      if (isPendingStudentVerificationResponse(responseData)) {
        routeToPendingVerification(data, submittedEmail);
        return;
      }

      if (normalizedStatus === "fail" || (statusCode && statusCode !== "200")) {
        toast.error(
          getLoginErrorMessage(responseData),
          {
            ariaLabel: "Login Error",
            autoClose: 2000,
            theme: "colored",
            hideProgressBar: true,
            className: "font-mali",
          }
        );
        return;
      }

      finalizeStudentLogin({ dispatch, navigate, profileData });
    },
    onError: (error, variables) => {
      const responseData = error?.response?.data || {};
      const submittedEmail = String(variables?.email || "").trim().toLowerCase();

      // Log login API error with response body
      logLoginApiResult(
        submittedEmail || "unknown@example.com",
        error?.response?.status || 500,
        "login",
        responseData,
        responseData?.message || GENERIC_LOGIN_ERROR
      );

      if (isPendingStudentVerificationResponse(responseData)) {
        routeToPendingVerification({ data: responseData }, submittedEmail);
        return;
      }

      toast.error(getLoginErrorMessage(responseData), {
        ariaLabel: "Login Error",
        autoClose: 2000,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });
    },
  });
};

export const useUpdateUser = () => {
  const dispatch = useDispatch();
  const { user: currentUser, token: currentToken } = useSelector(
    (state) => state.user
  );

  return useMutation({
    mutationKey: ["updateUser"],
    mutationFn: (updateData) => {
      var form_data = new FormData();

      for (var key in updateData) {
        form_data.append(key, updateData[key]);
      }
      return apiService.post("api/updatesignup", form_data);
    },
    onSuccess: (data) => {
      console.log("User updated successfully:", data);
      const updatedUser = data?.data?.data || {};
      const mergedUser = {
        ...currentUser,
        ...updatedUser,
      };
      const nextToken =
        updatedUser?.jwtoken || mergedUser?.jwtoken || currentToken;

      dispatch(
        setUser({ user: mergedUser, token: nextToken })
      );
    },
    onError: (error) => {
      console.error(
        "User update failed:",
        error.response?.data?.message || error.message
      );
    },
  });
};

export default useLogin;
